/**
 * Marks data that is synthetic (fabricated for demonstration) rather than measured.
 *
 * Some patients are monitored by a contactless sensor that supplies only heart
 * rate and respiratory rate, while the Visensia index needs at least three vital
 * modalities. The missing modality is generated so the platform can be
 * demonstrated end to end — but a fabricated reading, and any index derived from
 * one, must never be mistaken for a measurement.
 */
export const SYNTHETIC_SOURCE = 'synthetic_demo';

/** Inline chip for a single fabricated reading. */
export const DemoDataBadge = ({ title }: { title?: string }) => (
  <span
    title={title ?? 'Simulated value — not measured from this patient. For demonstration only.'}
    className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide
               bg-amber-100 text-amber-800 border border-amber-300 align-middle"
  >
    Demo data
  </span>
);

/** Banner for a derived value (e.g. a Health Index) computed partly from synthetic inputs. */
export const SyntheticInputsNotice = ({ modalities }: { modalities: string[] }) => {
  if (!modalities.length) return null;
  const pretty = modalities.map(m => m.replace(/_/g, ' ')).join(', ');
  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
      <p className="text-xs text-amber-900">
        <span className="font-semibold uppercase tracking-wide">Demo-assisted</span>{' '}
        — this index is computed partly from simulated data ({pretty}), because this patient's
        sensor does not supply enough measured vital signs to score. Not for clinical use.
      </p>
    </div>
  );
};

/**
 * Patient-level provenance banner.
 *
 * `is_demo` means the entire patient is fabricated. A real patient can still have
 * individual synthetic readings (the Emfit patients' blood pressure), which is a
 * different and weaker claim — hence two distinct notices rather than one.
 */
export const PatientDataProvenance = ({
  isDemo,
  syntheticModalities = [],
}: { isDemo: boolean; syntheticModalities?: string[] }) => {
  if (isDemo) {
    return (
      <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
        <p className="text-sm text-amber-900">
          <span className="font-semibold uppercase tracking-wide">Simulated patient</span>
          {' '}— this record is generated for demonstration. Every vital sign, sleep session and
          alert shown here is synthetic. Not a real person and not clinical data.
        </p>
      </div>
    );
  }
  if (syntheticModalities.length) {
    const pretty = syntheticModalities.map(m => m.replace(/_/g, ' ')).join(', ');
    return (
      <div className="mb-4 rounded-md border border-sky-300 bg-sky-50 px-3 py-2">
        <p className="text-sm text-sky-900">
          <span className="font-semibold uppercase tracking-wide">Real patient, mixed data</span>
          {' '}— sensor readings are genuine, but {pretty} is simulated to meet the Health Index
          minimum of three vital modalities. Simulated values are marked individually.
        </p>
      </div>
    );
  }
  return null;
};

export default DemoDataBadge;
